import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { Link } from "@/components/LocalizedLink";
import { useLanguage } from "@/contexts/LanguageContext";
import { useRef, useEffect } from "react";
import heroVisual from "@/assets/hero-visual.webp";
import MagneticButton from "@/components/MagneticButton";

type HeroProps = {
  primaryCtaText?: string;
  primaryCtaUrl?: string;
  secondaryCtaText?: string;
  secondaryCtaUrl?: string;
};

const FloatingOrb = ({
  delay,
  size,
  x,
  y,
}: {
  delay: number;
  size: number;
  x: string;
  y: string;
}) => (
  <motion.div
    className="absolute rounded-full bg-foreground/[0.03] backdrop-blur-sm"
    style={{ width: size, height: size, left: x, top: y }}
    initial={{ opacity: 0, scale: 0 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 1.5, delay }}
  >
    <motion.div
      className="w-full h-full rounded-full"
      animate={{ y: [0, -20, 0], x: [0, 10, 0] }}
      transition={{
        duration: 6 + delay * 2,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />
  </motion.div>
);

const Hero = ({
  primaryCtaText,
  primaryCtaUrl,
  secondaryCtaText,
  secondaryCtaUrl,
}: HeroProps) => {
  const { t } = useLanguage();
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });

  const sphereY = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const sphereScale = useTransform(scrollYProgress, [0, 1], [1, 0.6]);
  const sphereRotate = useTransform(scrollYProgress, [0, 1], [0, 45]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const textY = useTransform(scrollYProgress, [0, 0.3], [0, -80]);

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springX = useSpring(mouseX, { damping: 30, stiffness: 100 });
  const springY = useSpring(mouseY, { damping: 30, stiffness: 100 });

  useEffect(() => {
    const handleMouse = (e: MouseEvent) => {
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;
      mouseX.set((e.clientX - centerX) * 0.02);
      mouseY.set((e.clientY - centerY) * 0.02);
    };
    window.addEventListener("mousemove", handleMouse);
    return () => window.removeEventListener("mousemove", handleMouse);
  }, [mouseX, mouseY]);

  const resolvedPrimaryCtaUrl = primaryCtaUrl || "/contact";
  const resolvedSecondaryCtaUrl = secondaryCtaUrl || "/solutions";
  const isPrimaryExternal = resolvedPrimaryCtaUrl.startsWith("http");
  const isSecondaryExternal = resolvedSecondaryCtaUrl.startsWith("http");

  return (
    <section
      ref={sectionRef}
      className="relative min-h-[100svh] flex items-center justify-center overflow-hidden"
    >
      <FloatingOrb delay={0.5} size={120} x="10%" y="20%" />
      <FloatingOrb delay={0.8} size={80} x="80%" y="15%" />
      <FloatingOrb delay={1.1} size={60} x="75%" y="70%" />
      <FloatingOrb delay={1.4} size={100} x="5%" y="65%" />

      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage:
            "linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <motion.div
        className="relative z-10 w-full max-w-5xl mx-auto px-6 pt-24 pb-28 flex flex-col items-center text-center"
        style={{ opacity: textOpacity, y: textY }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.3, y: 60 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="mb-12 flex justify-center"
          style={{ y: sphereY, scale: sphereScale }}
        >
          <motion.div style={{ x: springX, y: springY }}>
            <motion.img
              src={heroVisual}
              alt="Figtures Studio"
              className="w-44 h-44 sm:w-56 sm:h-56 lg:w-72 lg:h-72 object-contain drop-shadow-2xl"
              width={800}
              height={800}
              // @ts-expect-error fetchpriority is a valid HTML attribute not yet in React's JSX types
              fetchpriority="high"
              style={{ rotate: sphereRotate }}
              animate={{ y: [0, -14, 0] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
            />
          </motion.div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="font-body text-[10px] sm:text-xs tracking-[0.28em] uppercase text-muted-foreground/80 mb-6"
        >
          {t("hero.tag")}
        </motion.p>

        <motion.h1
          className="font-display text-4xl sm:text-6xl lg:text-[5rem] xl:text-[6.5rem] font-bold leading-[1.08] tracking-[-0.04em] text-foreground w-full"
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="block tracking-wide">
            {t("hero.title1")} {t("hero.title2")}
          </span>
          <span className="block text-muted-foreground text-2xl sm:text-3xl lg:text-4xl font-semibold mt-4 tracking-wide">
            {t("hero.title3")}
          </span>
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.58 }}
          className="flex flex-wrap justify-center gap-4 mt-12"
        >
          <MagneticButton>
            {isPrimaryExternal ? (
              <a
                href={resolvedPrimaryCtaUrl}
                target="_blank"
                rel="noreferrer"
                className="font-body text-sm px-8 py-3.5 bg-foreground text-background rounded-full font-medium hover:opacity-90 transition-all duration-300 inline-block"
              >
                {primaryCtaText || t("hero.cta1")}
              </a>
            ) : (
              <Link
                to={resolvedPrimaryCtaUrl}
                className="font-body text-sm px-8 py-3.5 bg-foreground text-background rounded-full font-medium hover:opacity-90 transition-all duration-300 inline-block"
              >
                {primaryCtaText || t("hero.cta1")}
              </Link>
            )}
          </MagneticButton>
          <MagneticButton>
            {isSecondaryExternal ? (
              <a
                href={resolvedSecondaryCtaUrl}
                target="_blank"
                rel="noreferrer"
                className="font-body text-sm px-8 py-3.5 border border-border text-foreground rounded-full font-medium hover:border-foreground/30 transition-all duration-300 inline-block"
              >
                {secondaryCtaText || t("hero.cta2")}
              </a>
            ) : (
              <Link
                to={resolvedSecondaryCtaUrl}
                className="font-body text-sm px-8 py-3.5 border border-border text-foreground rounded-full font-medium hover:border-foreground/30 transition-all duration-300 inline-block"
              >
                {secondaryCtaText || t("hero.cta2")}
              </Link>
            )}
          </MagneticButton>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 3 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3"
      >
        <motion.span
          className="font-body text-[9px] tracking-[0.3em] text-muted-foreground/50 uppercase"
          animate={{ opacity: [0.3, 0.7, 0.3] }}
          transition={{ duration: 3, repeat: Infinity }}
        >
          SCROLL
        </motion.span>
        <motion.div
          className="w-px h-8 bg-foreground/10 origin-top"
          animate={{ scaleY: [0, 1, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>
    </section>
  );
};

export default Hero;
